import type { AxiosError, AxiosInstance } from 'axios';

import type { CreditAccount } from '@pray/shared/services/studioService/credits';

import { ENTERPRISE_PLAN_SKU } from 'constants.js';

import { useCreditsShortageModalStore } from '../components/pages/StudioPage/components/CreditsShortageModal/stores/creditsShortageModalStore';

const ERROR_CODES = {
  INSUFFICIENT_CREDITS: 'INSUFFICIENT_CREDITS',
};

// The confirmation dialog offers its own top-up when a confirmation is refused for a shortage.
const isCreditConfirmationRequest = (url?: string): boolean => !!url?.includes('/credit-confirmations');

let creditAccountRef: CreditAccount | null = null;

/**
 * Updates the credit account reference for use in error handling
 * @param creditAccount - The current credit account data
 */
export const updateCreditAccountRef = (creditAccount: typeof creditAccountRef) => {
  creditAccountRef = creditAccount;
};

/**
 * Sets up a global axios interceptor to handle specific errors globally
 * @param axiosInstance - The axios instance to attach the interceptor to
 * @returns A function to remove the interceptor
 */
export const setupErrorInterceptor = (axiosInstance: AxiosInstance): (() => void) => {
  const interceptorId = axiosInstance.interceptors.response.use(
    (response) => response,
    (
      error: AxiosError<{
        data?: {
          code?: string;
          credits_needed?: number;
          details?: {
            shortage_amount?: number;
          };
        };
      }>
    ) => {
      const responseData = error.response?.data?.data;

      if (responseData?.code === ERROR_CODES.INSUFFICIENT_CREDITS && !isCreditConfirmationRequest(error.config?.url)) {
        const shortageAmount = responseData.details?.shortage_amount;
        const neededCredits = shortageAmount ? Math.ceil(shortageAmount) : undefined;

        if (creditAccountRef?.creditsPlan?.sku === ENTERPRISE_PLAN_SKU) {
          const { openEnterpriseCreditsModal } = useCreditsShortageModalStore.getState();
          openEnterpriseCreditsModal(neededCredits);
        } else {
          if (!creditAccountRef?.creditsPlan?.isTrialActive) {
            const { openCreditsShortageModal } = useCreditsShortageModalStore.getState();
            openCreditsShortageModal(neededCredits);
          }

          if (creditAccountRef?.creditsPlan?.isTrialActive) {
            const { openTrialLimitationModal } = useCreditsShortageModalStore.getState();
            openTrialLimitationModal();
          }
        }
      }

      return Promise.reject(error);
    }
  );

  return () => {
    axiosInstance.interceptors.response.eject(interceptorId);
  };
};
